import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db, auth } from '../firebase/config';
import { initializeApp, getApps, deleteApp } from 'firebase/app';
import {
  collection, addDoc, doc, deleteDoc, updateDoc,
  setDoc, query, where, orderBy, onSnapshot, getDocs
} from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function AdminDashboard() {
  const { logout, currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [clubs, setClubs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedClub, setSelectedClub] = useState(null);
  const [clubPlayers, setClubPlayers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [newClubName, setNewClubName] = useState('');
  const [newClubEmail, setNewClubEmail] = useState('');
  const [newClubPassword, setNewClubPassword] = useState('');
  const [newClubLogoFile, setNewClubLogoFile] = useState(null);
  const [addingClub, setAddingClub] = useState(false);
  const [clubMsg, setClubMsg] = useState('');
  const [uploadingLogoFor, setUploadingLogoFor] = useState(null);
  const [selectedSessionIds, setSelectedSessionIds] = useState([]);
  const [sessionsClubFilter, setSessionsClubFilter] = useState('');
  const [newPlayerName, setNewPlayerName] = useState('');
  const [newPlayerGroup, setNewPlayerGroup] = useState('A');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const months = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

  useEffect(() => {
    const unsubClubs = onSnapshot(collection(db, 'clubs'), snap => {
      setClubs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const unsubSessions = onSnapshot(
      query(collection(db, 'sessions'), orderBy('timestamp', 'desc')),
      snap => setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return () => { unsubClubs(); unsubSessions(); };
  }, []);

  useEffect(() => {
    if (!selectedClub) return;
    const unsubPlayers = onSnapshot(
      collection(db, 'clubs', selectedClub.id, 'players'),
      snap => setClubPlayers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    const unsubAttendance = onSnapshot(
      query(collection(db, 'attendance'), where('clubId', '==', selectedClub.id)),
      snap => setAttendance(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return () => { unsubPlayers(); unsubAttendance(); };
  }, [selectedClub]);

  async function createClub() {
    if (!newClubName || !newClubEmail || !newClubPassword) {
      setClubMsg('Bitte alle Felder ausfüllen!');
      return;
    }
    setAddingClub(true);
    setClubMsg('');

    // Use a SECONDARY, temporary Firebase app instance to create the new
    // club's login. This prevents the admin's own session from being
    // replaced/logged out, which happens if you call
    // createUserWithEmailAndPassword on the main `auth` instance.
    const secondaryAppName = 'SecondaryAppForClubCreation';
    let secondaryApp;
    // Clean up any leftover secondary app from a previous failed attempt
    const existing = getApps().find(a => a.name === secondaryAppName);
    if (existing) {
      await deleteApp(existing);
    }
    secondaryApp = initializeApp(auth.app.options, secondaryAppName);
    const secondaryAuth = getAuth(secondaryApp);

    try {
      // Step 1: Create club document first
      const clubRef = await addDoc(collection(db, 'clubs'), {
        name: newClubName,
        email: newClubEmail,
        createdAt: new Date(),
        active: true
      });

      // Step 2: Create Firebase Auth user on the SECONDARY auth instance
      // (does not affect the currently logged-in admin)
      const userCred = await createUserWithEmailAndPassword(secondaryAuth, newClubEmail, newClubPassword);
      const uid = userCred.user.uid;

      // Step 3: Create user document
      await setDoc(doc(db, 'users', uid), {
        role: 'club',
        clubId: clubRef.id,
        email: newClubEmail
      });

      // Step 3b: Compress and save logo as base64 if one was selected
      if (newClubLogoFile) {
        const logoUrl = await compressImageToBase64(newClubLogoFile);
        await updateDoc(doc(db, 'clubs', clubRef.id), { logoUrl });
      }

      // Step 4: Sign out of the secondary auth instance and delete it —
      // the admin's real session (on the main `auth`) is untouched throughout
      await signOut(secondaryAuth);
      await deleteApp(secondaryApp);

      setClubMsg(`✅ Verein "${newClubName}" erstellt! Form-Link: ${window.location.origin}/form/${clubRef.id}`);
      setNewClubName('');
      setNewClubEmail('');
      setNewClubPassword('');
      setNewClubLogoFile(null);
    } catch (err) {
      setClubMsg('Fehler: ' + err.message);
      // Make sure we still clean up the secondary app on failure
      try { await deleteApp(secondaryApp); } catch (_) {}
    }
    setAddingClub(false);
  }

  async function deleteClub(clubId) {
    if (!window.confirm('Verein wirklich löschen? Dies entfernt auch alle Spieler, Einheiten und den Zugang des Vereins.')) return;
    try {
      // 1. Delete the club document itself
      await deleteDoc(doc(db, 'clubs', clubId));

      // 2. Delete all players belonging to this club
      const playersSnap = await getDocs(collection(db, 'clubs', clubId, 'players'));
      await Promise.all(playersSnap.docs.map(p => deleteDoc(doc(db, 'clubs', clubId, 'players', p.id))));

      // 3. Delete all sessions belonging to this club
      const sessionsSnap = await getDocs(query(collection(db, 'sessions'), where('clubId', '==', clubId)));
      await Promise.all(sessionsSnap.docs.map(s => deleteDoc(doc(db, 'sessions', s.id))));

      // 4. Delete all attendance records belonging to this club
      const attendanceSnap = await getDocs(query(collection(db, 'attendance'), where('clubId', '==', clubId)));
      await Promise.all(attendanceSnap.docs.map(a => deleteDoc(doc(db, 'attendance', a.id))));

      // 5. Delete the users/{uid} document that links this club's login to its role/clubId.
      // This blocks the club from loading any dashboard even though their Auth
      // login technically still exists (see note below — full Auth deletion
      // requires a Cloud Function, not client-side code).
      const usersSnap = await getDocs(query(collection(db, 'users'), where('clubId', '==', clubId)));
      await Promise.all(usersSnap.docs.map(u => deleteDoc(doc(db, 'users', u.id))));

      if (selectedClub?.id === clubId) setSelectedClub(null);
    } catch (err) {
      alert('Fehler beim Löschen: ' + err.message);
    }
  }

  async function addPlayer() {
    if (!selectedClub || !newPlayerName.trim()) {
      alert('Bitte Spielernamen eingeben!');
      return;
    }
    try {
      await addDoc(collection(db, 'clubs', selectedClub.id, 'players'), {
        name: newPlayerName.trim(),
        group: newPlayerGroup,
        createdAt: new Date()
      });
      setNewPlayerName('');
      alert('Spieler hinzugefügt!');
    } catch (err) {
      alert('Fehler: ' + err.message);
    }
  }

  async function handleExistingClubLogoUpload(clubId, file) {
    if (!file) return;
    setUploadingLogoFor(clubId);
    try {
      const logoUrl = await compressImageToBase64(file);
      await updateDoc(doc(db, 'clubs', clubId), { logoUrl });
    } catch (err) {
      alert('Fehler beim Hochladen des Logos: ' + err.message);
    }
    setUploadingLogoFor(null);
  }

  // Resizes an image client-side and returns it as a small base64 string,
  // so it can be stored directly in the club's Firestore document —
  // no Firebase Storage / Blaze plan required.
  function compressImageToBase64(file, maxDimension = 200, quality = 0.7) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;
          if (width > height && width > maxDimension) {
            height = Math.round(height * (maxDimension / width));
            width = maxDimension;
          } else if (height > maxDimension) {
            width = Math.round(width * (maxDimension / height));
            height = maxDimension;
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function toggleSessionSelected(sessionId) {
    setSelectedSessionIds(prev =>
      prev.includes(sessionId) ? prev.filter(id => id !== sessionId) : [...prev, sessionId]
    );
  }

  function toggleSelectAllSessions(visibleIds) {
    const allSelected = visibleIds.every(id => selectedSessionIds.includes(id));
    setSelectedSessionIds(allSelected ? [] : visibleIds);
  }

  async function deleteSelectedSessions() {
    if (selectedSessionIds.length === 0) return;
    if (!window.confirm(`${selectedSessionIds.length} Einheit(en) wirklich löschen? Das kann nicht rückgängig gemacht werden.`)) return;
    try {
      await Promise.all(selectedSessionIds.map(id => deleteDoc(doc(db, 'sessions', id))));
      setSelectedSessionIds([]);
    } catch (err) {
      alert('Fehler beim Löschen: ' + err.message);
    }
  }

  async function startNewCycle(clubId, clubName) {
    if (!window.confirm(
      `Neuen Zyklus für "${clubName}" starten? Dies löscht ALLE Einheiten und Anwesenheitsdaten dieses Vereins unwiderruflich. Spieler bleiben erhalten. Fortfahren?`
    )) return;
    try {
      const sessionsSnap = await getDocs(query(collection(db, 'sessions'), where('clubId', '==', clubId)));
      await Promise.all(sessionsSnap.docs.map(s => deleteDoc(doc(db, 'sessions', s.id))));

      const attendanceSnap = await getDocs(query(collection(db, 'attendance'), where('clubId', '==', clubId)));
      await Promise.all(attendanceSnap.docs.map(a => deleteDoc(doc(db, 'attendance', a.id))));

      alert(`Neuer Zyklus für "${clubName}" gestartet. Alle alten Einheiten und Anwesenheitsdaten wurden gelöscht.`);
    } catch (err) {
      alert('Fehler beim Zurücksetzen: ' + err.message);
    }
  }

  async function deletePlayer(playerId) {
    await deleteDoc(doc(db, 'clubs', selectedClub.id, 'players', playerId));
  }

  function calcACWR(player, clubId) {
    const playerSessions = sessions.filter(s =>
      (s.playerId ? s.playerId === player.id : s.playerName === player.name) && s.clubId === clubId
    );
    const now = new Date();
    const oneWeekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
    const fourWeeksAgo = new Date(now - 28 * 24 * 60 * 60 * 1000);
    const acute = playerSessions.filter(s => new Date(s.timestamp?.toDate?.() || s.timestamp) >= oneWeekAgo).reduce((sum, s) => sum + (s.load || 0), 0);
    const chronic = playerSessions.filter(s => new Date(s.timestamp?.toDate?.() || s.timestamp) >= fourWeeksAgo).reduce((sum, s) => sum + (s.load || 0), 0) / 4;
    return chronic > 0 ? (acute / chronic).toFixed(2) : null;
  }

  function getACWRBadge(acwr) {
    if (!acwr) return <span style={{color:'#999'}}>—</span>;
    const val = parseFloat(acwr);
    if (val < 0.8) return <span className="badge badge-purple">Unterforderung ({acwr})</span>;
    if (val <= 1.3) return <span className="badge badge-green">Sweet Spot ({acwr})</span>;
    if (val <= 1.49) return <span className="badge badge-yellow">Erhöht ({acwr})</span>;
    return <span className="badge badge-red">⚠️ Gefahr ({acwr})</span>;
  }

  function getLoadBadge(load) {
    if (!load) return <span style={{color:'#999'}}>—</span>;
    if (load < 500) return <span className="badge badge-purple">{load}</span>;
    if (load < 700) return <span className="badge badge-green">{load}</span>;
    if (load <= 900) return <span className="badge badge-yellow">{load}</span>;
    return <span className="badge badge-red">{load}</span>;
  }

  function getWeeklyChartData() {
    if (!selectedClub) return [];
    const clubSessions = sessions.filter(s => s.clubId === selectedClub.id);
    const weeks = {};
    clubSessions.forEach(s => {
      const week = s.week || 'Unbekannt';
      if (!weeks[week]) weeks[week] = 0;
      weeks[week] += s.load || 0;
    });
    return Object.entries(weeks).map(([week, total]) => ({ week, total })).slice(0, 10);
  }

  async function toggleAttendance(playerId, playerName, day) {
    if (!selectedClub) return;
    const key = `${selectedClub.id}_${playerId}_${selectedMonth}_${day}`;
    const existing = attendance.find(a => a.key === key);
    if (existing) {
      await deleteDoc(doc(db, 'attendance', existing.id));
    } else {
      await setDoc(doc(db, 'attendance', key), {
        key, clubId: selectedClub.id, playerId, playerName,
        month: selectedMonth, day, present: true, createdAt: new Date()
      });
    }
  }

  function isPresent(playerId, day) {
    if (!selectedClub) return false;
    const key = `${selectedClub.id}_${playerId}_${selectedMonth}_${day}`;
    return attendance.some(a => a.key === key);
  }

  function getAttendanceCount(playerId) {
    if (!selectedClub) return 0;
    return attendance.filter(a => a.playerId === playerId && a.clubId === selectedClub.id && a.month === selectedMonth).length;
  }

  const daysInMonth = new Date(new Date().getFullYear(), selectedMonth + 1, 0).getDate();

  return (
    <div className="app-container">
      <div className="navbar">
        <div>
          <div className="navbar-title">RPE System — Admin</div>
          <div className="navbar-subtitle">Alle Vereine & Spieler</div>
        </div>
        <div className="navbar-right">
          <span style={{fontSize:13,color:'#888'}}>{currentUser?.email}</span>
          <button className="btn-logout" onClick={logout}>Abmelden</button>
        </div>
      </div>

      <div className="page-content">
        <div className="stats-row">
          <div className="stat-card"><div className="stat-number">{clubs.length}</div><div className="stat-label">Aktive Vereine</div></div>
          <div className="stat-card"><div className="stat-number">{sessions.length}</div><div className="stat-label">Einheiten gesamt</div></div>
          <div className="stat-card"><div className="stat-number">{sessions.filter(s=>{const d=s.timestamp?.toDate?.()||new Date(s.timestamp);return(new Date()-d)<7*24*60*60*1000;}).length}</div><div className="stat-label">Einheiten diese Woche</div></div>
          <div className="stat-card"><div className="stat-number">{sessions.filter(s=>s.load>900).length}</div><div className="stat-label">🔴 Kritische Einheiten</div></div>
        </div>

        <div className="tabs">
          {['overview','clubs','players','sessions','attendance'].map(tab=>(
            <button key={tab} className={`tab ${activeTab===tab?'active':''}`} onClick={()=>setActiveTab(tab)}>
              {tab==='overview'&&'📊 Übersicht'}{tab==='clubs'&&'🏟️ Vereine'}{tab==='players'&&'👥 Spieler'}{tab==='sessions'&&'📋 Einheiten'}{tab==='attendance'&&'📅 Anwesenheit'}
            </button>
          ))}
        </div>

        {activeTab==='overview'&&(
          <div className="card">
            <div className="card-title">Alle Vereine — Übersicht</div>
            <div className="table-wrapper">
              <table>
                <thead><tr><th className="sticky-col">Verein</th><th>Einheiten gesamt</th><th>Diese Woche</th><th>Form-Link</th></tr></thead>
                <tbody>
                  {clubs.map(club=>{
                    const cs=sessions.filter(s=>s.clubId===club.id);
                    const tw=cs.filter(s=>{const d=s.timestamp?.toDate?.()||new Date(s.timestamp);return(new Date()-d)<7*24*60*60*1000;});
                    return(<tr key={club.id}>
                      <td className="sticky-col" style={{fontWeight:'bold',textAlign:'left'}}>{club.name}</td>
                      <td>{cs.length}</td><td>{tw.length}</td>
                      <td><code style={{fontSize:11,background:'#f5f5f5',padding:'2px 6px',borderRadius:4,wordBreak:'break-all',display:'inline-block',maxWidth:180}}>{window.location.origin}/form/{club.id}</code></td>
                    </tr>);
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab==='clubs'&&(
          <div>
            <div className="card">
              <div className="card-title">Neuen Verein erstellen</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
                <div className="form-group"><label>Vereinsname</label><input value={newClubName} onChange={e=>setNewClubName(e.target.value)} placeholder="FC Beispiel"/></div>
                <div className="form-group"><label>Login E-Mail</label><input type="email" value={newClubEmail} onChange={e=>setNewClubEmail(e.target.value)} placeholder="verein@email.com"/></div>
                <div className="form-group"><label>Passwort</label><input type="password" value={newClubPassword} onChange={e=>setNewClubPassword(e.target.value)} placeholder="••••••••"/></div>
              </div>
              <div className="form-group" style={{marginTop:12}}>
                <label>Vereinslogo (optional)</label>
                <input type="file" accept="image/*" onChange={e=>setNewClubLogoFile(e.target.files[0]||null)} />
              </div>
              <button className="btn-primary" onClick={createClub} disabled={addingClub}>{addingClub?'Wird erstellt...':'+ Verein erstellen'}</button>
              {clubMsg&&<div style={{marginTop:12,padding:12,background:'#f5f5f5',borderRadius:6,fontSize:13,wordBreak:'break-all'}}>{clubMsg}</div>}
            </div>
            <div className="card">
              <div className="card-title">Alle Vereine ({clubs.length})</div>
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Logo</th><th className="sticky-col">Vereinsname</th><th>E-Mail</th><th>Form-Link für Spieler</th><th>Aktionen</th></tr></thead>
                  <tbody>
                    {clubs.map(club=>(
                      <tr key={club.id}>
                        <td>
                          {club.logoUrl
                            ? <img src={club.logoUrl} alt={club.name} style={{width:36,height:36,objectFit:'contain',borderRadius:6}} />
                            : <span style={{color:'#CCC',fontSize:12}}>—</span>}
                          <label style={{display:'block',marginTop:4,fontSize:11,color:'#C0392B',cursor:'pointer'}}>
                            {uploadingLogoFor===club.id ? 'Lädt...' : 'Ändern'}
                            <input
                              type="file"
                              accept="image/*"
                              style={{display:'none'}}
                              onChange={e=>handleExistingClubLogoUpload(club.id, e.target.files[0])}
                            />
                          </label>
                        </td>
                        <td className="sticky-col" style={{fontWeight:'bold',textAlign:'left'}}>{club.name}</td>
                        <td>{club.email}</td>
                        <td><code style={{fontSize:11,wordBreak:'break-all',display:'inline-block',maxWidth:180}}>{window.location.origin}/form/{club.id}</code></td>
                        <td>
                          <button className="btn-secondary" style={{fontSize:12,padding:'6px 12px',marginRight:8}} onClick={()=>{setSelectedClub(club);setActiveTab('players');}}>Spieler</button>
                          <button
                            onClick={()=>startNewCycle(club.id, club.name)}
                            style={{fontSize:12,padding:'6px 12px',marginRight:8,border:'1px solid #7D6608',borderRadius:6,background:'#FFF',color:'#7D6608',cursor:'pointer',fontWeight:'bold'}}
                          >
                            Neuer Zyklus
                          </button>
                          <button className="btn-danger" onClick={()=>deleteClub(club.id)}>Löschen</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab==='players'&&(
          <div>
            <div className="card">
              <div className="card-title">Verein auswählen</div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {clubs.map(club=>(<button key={club.id} className={selectedClub?.id===club.id?'btn-primary':'btn-secondary'} onClick={()=>setSelectedClub(club)}>{club.name}</button>))}
              </div>
            </div>
            {selectedClub&&(
              <>
                <div className="card">
                  <div className="card-title">Spieler hinzufügen — {selectedClub.name}</div>
                  <div style={{display:'flex',gap:12,alignItems:'flex-end'}}>
                    <div className="form-group" style={{flex:1,marginBottom:0}}><label>Spielername</label><input value={newPlayerName} onChange={e=>setNewPlayerName(e.target.value)} placeholder="Max Mustermann" onKeyPress={e=>e.key==='Enter'&&addPlayer()}/></div>
                    <div className="form-group" style={{marginBottom:0}}><label>Gruppe</label><select value={newPlayerGroup} onChange={e=>setNewPlayerGroup(e.target.value)}><option value="A">A</option><option value="B">B</option><option value="C">C</option></select></div>
                    <button className="btn-primary" onClick={addPlayer}>+ Hinzufügen</button>
                  </div>
                </div>
                <div className="card">
                  <div className="card-title">Spieler & ACWR — {selectedClub.name} ({clubPlayers.length} Spieler)</div>
                  <div className="table-wrapper">
                    <table>
                      <thead><tr><th className="sticky-col">Name</th><th>Gruppe</th><th>ACWR</th><th>Einheiten</th><th>Aktionen</th></tr></thead>
                      <tbody>
                        {clubPlayers.map(player=>{
                          const ps=sessions.filter(s=>(s.playerId?s.playerId===player.id:s.playerName===player.name)&&s.clubId===selectedClub.id);
                          const acwr=calcACWR(player,selectedClub.id);
                          return(<tr key={player.id}>
                            <td className="sticky-col" style={{textAlign:'left',fontWeight:'bold'}}>{player.name}</td>
                            <td>{player.group}</td>
                            <td>{getACWRBadge(acwr)}</td>
                            <td>{ps.length}</td>
                            <td><button className="btn-danger" onClick={()=>deletePlayer(player.id)}>Entfernen</button></td>
                          </tr>);
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                {getWeeklyChartData().length>0&&(
                  <div className="card">
                    <div className="card-title">Wochenbelastung — {selectedClub.name}</div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={getWeeklyChartData()}><CartesianGrid strokeDasharray="3 3"/><XAxis dataKey="week" fontSize={12}/><YAxis fontSize={12}/><Tooltip/><Bar dataKey="total" fill="#C0392B" radius={[4,4,0,0]}/></BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab==='sessions'&&(
          <div className="card">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:12,marginBottom:12}}>
              <div className="card-title" style={{marginBottom:0}}>Alle Einheiten</div>
              {selectedSessionIds.length > 0 && (
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <span style={{fontSize:13,color:'#666'}}>{selectedSessionIds.length} ausgewählt</span>
                  <button
                    onClick={deleteSelectedSessions}
                    style={{padding:'8px 16px',border:'none',borderRadius:6,background:'#C0392B',color:'#FFF',cursor:'pointer',fontSize:13,fontWeight:'bold'}}
                  >
                    Löschen
                  </button>
                </div>
              )}
            </div>
            <div style={{marginBottom:16}}>
              <label style={{fontSize:13,fontWeight:'bold',marginRight:8}}>Verein:</label>
              <select
                value={sessionsClubFilter}
                onChange={e=>{setSessionsClubFilter(e.target.value);setSelectedSessionIds([]);}}
                style={{padding:'8px 12px',borderRadius:6,border:'1px solid #DDD',fontSize:16}}
              >
                <option value="">Alle Vereine</option>
                {clubs.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="table-wrapper">
              <table>
                <thead><tr>
                  <th style={{width:32}}>
                    <input
                      type="checkbox"
                      checked={sessions.filter(s=>!sessionsClubFilter||s.clubId===sessionsClubFilter).slice(0,100).length>0 && sessions.filter(s=>!sessionsClubFilter||s.clubId===sessionsClubFilter).slice(0,100).every(s=>selectedSessionIds.includes(s.id))}
                      onChange={()=>toggleSelectAllSessions(sessions.filter(s=>!sessionsClubFilter||s.clubId===sessionsClubFilter).slice(0,100).map(s=>s.id))}
                    />
                  </th>
                  <th>Datum</th><th>Verein</th><th className="sticky-col">Spieler</th><th>Art</th><th>Dauer</th><th>RPE</th><th>Belastung (AU)</th><th>Wohlbefinden</th><th>Woche</th>
                </tr></thead>
                <tbody>
                  {sessions.filter(s=>!sessionsClubFilter||s.clubId===sessionsClubFilter).slice(0,100).map(s=>{
                    const clubName=clubs.find(c=>c.id===s.clubId)?.name||s.clubId;
                    const d=s.timestamp?.toDate?.()||new Date(s.timestamp);
                    return(<tr key={s.id} style={selectedSessionIds.includes(s.id)?{background:'#FDEDEC'}:undefined}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedSessionIds.includes(s.id)}
                          onChange={()=>toggleSessionSelected(s.id)}
                        />
                      </td>
                      <td style={{fontSize:12}}>{d.toLocaleDateString('de-AT')}</td>
                      <td style={{fontSize:12}}>{clubName}</td>
                      <td className="sticky-col" style={{textAlign:'left',fontWeight:'bold'}}>{s.playerName}</td>
                      <td>{s.sessionType}</td><td>{s.duration} Min</td><td>{s.rpe}</td>
                      <td>{getLoadBadge(s.load)}</td>
                      <td style={{fontSize:12}}>{s.wellness}</td>
                      <td style={{fontSize:12}}>{s.week}</td>
                    </tr>);
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab==='attendance'&&(
          <div>
            <div className="card">
              <div className="card-title">Anwesenheitsliste</div>
              <div style={{display:'flex',gap:12,alignItems:'center',marginBottom:16,flexWrap:'wrap'}}>
                <div>
                  <label style={{fontSize:13,fontWeight:'bold',marginRight:8}}>Verein:</label>
                  <select onChange={e=>setSelectedClub(clubs.find(c=>c.id===e.target.value)||null)} style={{padding:'8px 12px',borderRadius:6,border:'1px solid #DDD',fontSize:16}}>
                    <option value="">Verein auswählen</option>
                    {clubs.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontSize:13,fontWeight:'bold',marginRight:8}}>Monat:</label>
                  <select value={selectedMonth} onChange={e=>setSelectedMonth(parseInt(e.target.value))} style={{padding:'8px 12px',borderRadius:6,border:'1px solid #DDD',fontSize:16}}>
                    {months.map((m,i)=><option key={i} value={i}>{m}</option>)}
                  </select>
                </div>
              </div>
              {selectedClub&&clubPlayers.length>0&&(
                <div className="table-wrapper">
                  <table>
                    <thead><tr><th className="sticky-col" style={{textAlign:'left',minWidth:140}}>Spieler</th>{Array.from({length:daysInMonth},(_,i)=>(<th key={i} style={{minWidth:32,fontSize:11}}>{i+1}</th>))}<th>∑</th><th>%</th></tr></thead>
                    <tbody>
                      {clubPlayers.map(player=>(
                        <tr key={player.id}>
                          <td className="sticky-col" style={{textAlign:'left',fontWeight:'bold',fontSize:12}}>{player.name}</td>
                          {Array.from({length:daysInMonth},(_,i)=>(
                            <td key={i} style={{padding:4}}>
                              <button onClick={()=>toggleAttendance(player.id,player.name,i+1)} style={{width:24,height:24,border:'none',borderRadius:4,cursor:'pointer',background:isPresent(player.id,i+1)?'#C0392B':'#EEE',color:isPresent(player.id,i+1)?'#FFF':'#999',fontSize:11,fontWeight:'bold'}}>
                                {isPresent(player.id,i+1)?'✓':''}
                              </button>
                            </td>
                          ))}
                          <td style={{fontWeight:'bold'}}>{getAttendanceCount(player.id)}</td>
                          <td style={{fontSize:12,color:'#666'}}>{Math.round((getAttendanceCount(player.id)/daysInMonth)*100)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!selectedClub&&<p style={{color:'#888',fontSize:14,textAlign:'center',padding:20}}>Bitte zuerst einen Verein auswählen</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}